import { createContext, useContext, useState, useCallback } from 'react'

export type DateRangePreset =
  | 'today' | 'yesterday' | 'last7' | 'last30'
  | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter'
  | 'thisYear' | 'lastYear' | 'allTime' | 'custom'

export interface DateRange {
  start: Date | null
  end: Date | null
  preset: DateRangePreset
  label: string
}

interface DateRangeCtx {
  range: DateRange
  setRange: (r: DateRange) => void
  inRange: (dateStr: string) => boolean
}

// ── Preset calculator ──────────────────────────────────────────
export function buildPreset(preset: DateRangePreset, customStart?: Date | null, customEnd?: Date | null): DateRange {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)

  function startOf(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()) }
  function endOf(d: Date)   { return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59) }
  function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x }

  switch (preset) {
    case 'today':
      return { start: today, end: endOfToday, preset, label: 'Today' }

    case 'yesterday': {
      const y = addDays(today, -1)
      return { start: startOf(y), end: endOf(y), preset, label: 'Yesterday' }
    }

    case 'last7': {
      const s = addDays(today, -6)
      return { start: startOf(s), end: endOfToday, preset, label: 'Last 7 days' }
    }

    case 'last30': {
      const s = addDays(today, -29)
      return { start: startOf(s), end: endOfToday, preset, label: 'Last 30 days' }
    }

    case 'thisMonth': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1)
      return { start: s, end: endOfToday, preset, label: 'This month' }
    }

    case 'lastMonth': {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const e = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59)
      return { start: s, end: e, preset, label: 'Last month' }
    }

    case 'thisQuarter': {
      const q = Math.floor(today.getMonth() / 3)
      const s = new Date(today.getFullYear(), q * 3, 1)
      return { start: s, end: endOfToday, preset, label: 'This quarter' }
    }

    case 'lastQuarter': {
      const q = Math.floor(today.getMonth() / 3)
      const prevQ = q === 0 ? 3 : q - 1
      const year = q === 0 ? today.getFullYear() - 1 : today.getFullYear()
      const s = new Date(year, prevQ * 3, 1)
      const e = new Date(year, prevQ * 3 + 3, 0, 23, 59, 59)
      return { start: s, end: e, preset, label: 'Last quarter' }
    }

    case 'thisYear': {
      const s = new Date(today.getFullYear(), 0, 1)
      return { start: s, end: endOfToday, preset, label: `This year (${today.getFullYear()})` }
    }

    case 'lastYear': {
      const s = new Date(today.getFullYear() - 1, 0, 1)
      const e = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59)
      return { start: s, end: e, preset, label: `Last year (${today.getFullYear() - 1})` }
    }

    case 'allTime':
      return { start: null, end: null, preset, label: 'All time' }

    case 'custom': {
      const s = customStart ?? null
      const e = customEnd ?? null
      const fmt = (d: Date | null) => d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '?'
      return { start: s, end: e ? new Date(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59) : null, preset, label: `${fmt(s)} – ${fmt(e)}` }
    }
  }
}

const DEFAULT_RANGE = buildPreset('thisMonth')

const Ctx = createContext<DateRangeCtx>({
  range: DEFAULT_RANGE,
  setRange: () => {},
  inRange: () => true,
})

const STORAGE_KEY = 'cb_date_range_preset'

function loadPersistedRange(): DateRange {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_RANGE
    const { preset, customStart, customEnd } = JSON.parse(raw) as {
      preset: DateRangePreset; customStart?: string; customEnd?: string
    }
    if (preset === 'custom') {
      return buildPreset('custom', customStart ? new Date(customStart) : null, customEnd ? new Date(customEnd) : null)
    }
    return buildPreset(preset)
  } catch { return DEFAULT_RANGE }
}

export function DateRangeProvider({ children }: { children: React.ReactNode }) {
  const [range, setRangeState] = useState<DateRange>(() => loadPersistedRange())

  const setRange = useCallback((r: DateRange) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        preset: r.preset,
        customStart: r.start?.toISOString(),
        customEnd:   r.end?.toISOString(),
      }))
    } catch { /* ignore */ }
    setRangeState(r)
  }, [])

  const inRange = useCallback((dateStr: string): boolean => {
    if (!range.start && !range.end) return true
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return true
    if (range.start && d < range.start) return false
    if (range.end   && d > range.end)   return false
    return true
  }, [range])

  return <Ctx.Provider value={{ range, setRange, inRange }}>{children}</Ctx.Provider>
}

export function useDateRange() { return useContext(Ctx) }
